import type { CognitiveOperationTypeForImage, ReprocessActionType, UserOperationType } from './asyncOperationPrimitives';
import type { AsyncOperationType } from './asyncOperationType';
import type { DynamoItemType } from './itemTypes';
import { FieldType } from '../mdf/fieldDefinition';
import { type MdfDiff, type MdfEntity, getEmptyMdfViewsObject } from './mdfModels';
import type { AnalyticsPluginConfig } from './itemPropTypes';
export type AnalyticsResult = {
  type?: 'system';
  operation: AsyncOperationType;
  status: string;
  errorMessage: string;
} | {
  type: 'plugins';
  analyticPlugins?: Record<string, AnalyticsPluginConfig>;
};

type ItemAnalyticsGenericParams = { analyticsParamsType?: undefined } & Record<string, string | string[] | number | number[] | boolean | boolean[] | undefined>;

export type ItemAnalyticsParamsForShareMetadata = {
  analyticsParamsType: 'share';
  shareId: string;
} | {
  analyticsParamsType: 'share_enhanced';
  shareId: string;
  externalUserId: string;
  externalUserEmail: string;
};

export type ItemAnalyticsParameters = ItemAnalyticsGenericParams | ItemAnalyticsParamsForShareMetadata;

export interface ItemAnalyticsDto {
  id: string;
  itemId: string;
  tenantId: string;
  actionType: AsyncOperationType | UserOperationType | ReprocessActionType | CognitiveOperationTypeForImage;
  executedBy: string;
  email: string | undefined;
  executedOn: string | undefined;
  provider: string | undefined;
  relatedParameters: ItemAnalyticsParameters | undefined;
  itemTitle: string | undefined;
  bulkOperationId: string | undefined;
  mdfDiff: MdfDiff | undefined;
  storageId: string | undefined;
  /**
   * If an item was created by copying information from another item, then an entry is created in the
   * item analytics history and this property will contain the ID of the item the data was copied from.
   */
  metadataCopiedFromItemId: string | undefined;
  highresFileDeletedUpdate: {
    type: 'kelda_on_premise',
    keldaId: string,
  } | undefined,
  highresFileRestoredUpdate: {
    type: 'kelda_on_premise',
    keldaId: string,
  } | undefined,
}

export interface ItemAnalyticsCreationDto {
  itemId: string;
  tenantId: string;
  actionType: AsyncOperationType | UserOperationType | ReprocessActionType | CognitiveOperationTypeForImage;
  executedBy: string;
  provider?: string;
  relatedParameters?: ItemAnalyticsParameters;
  bulkOperationId?: string;
  mdfDiff?: MdfDiff;
  storageId?: string;
  metadataCopiedFromItemId?: string;
  highresFileDeletedUpdate?: {
    type: 'kelda_on_premise',
    keldaId: string,
  },
  highresFileRestoredUpdate?: {
    type: 'kelda_on_premise',
    keldaId: string,
  },
}

export interface AnalyticsSearchQuery {
  itemsPerPage?: number;
  from: number;

  // TODO: Verify this is not used and remove support for it
  searchString: undefined;

  sortBy?: string;
  sortDesc?: boolean;
  // true means that we want to see all the analytics for all the tenants
  // instead of just the operations for the current tenant.
  // This requires super admin permission
  all: boolean;
  itemId?: string;
  date?: string;
  supportTenantId?: string;
}
export interface AnalyticsSearchResults {
  // Capped at 10000 By OpenSearch
  total: number;

  hits: ItemAnalyticsDto[];
}

const itemResources = <const>['highres', 'webProxy', 'editProxy', 'transcript'];
type ItemResource = typeof itemResources[number];

type AnalyticsPluginBase = {
  id: string;
  label: string;
  description: string;
  inputParameters: MdfEntity;
  // TODO: Add support for subtypes

  requiredResources: ItemResource[];

  compatibleItemTypes: DynamoItemType[] | undefined;
};

type InternalAnalyticsPlugin = AnalyticsPluginBase & { type: 'internal' };

export type AnalyticsPlugin = InternalAnalyticsPlugin;

export const sceneChangeDetectionPluginId = 'scene change detection';
export const sceneChangeDetectionThresholdFieldId = 'threshold';
export const sceneChangeDetectionDefaultThreshold = 3;
export const sceneChangeDetectionPlugin: AnalyticsPlugin = {
  type: 'internal',
  id: sceneChangeDetectionPluginId,
  label: 'Scene Change Detection',
  description: 'Adds log events to the item for each detected scene change',
  inputParameters: {
    id: 'shotChangeDetectionSettings',
    label: 'Shot change detection settings',
    active: true,
    fields: [
      {
        id: sceneChangeDetectionThresholdFieldId,
        fieldId: sceneChangeDetectionThresholdFieldId,
        type: FieldType.Number,
        defaultValue: sceneChangeDetectionDefaultThreshold,
        required: true,
      }
    ],
    views: {
      ...getEmptyMdfViewsObject(),
      item: [
        {
          id: sceneChangeDetectionThresholdFieldId,
          fieldId: sceneChangeDetectionThresholdFieldId,
          label: 'Change detection threshold on a scale of 1 to 10',
          visible: true,
        },
      ],
    },
    viewSections: undefined
  },
  requiredResources: [],
  compatibleItemTypes: ['video'],
};

export const SCENE_DETECTION_FILE_NAME = 'sceneDetection.txt';

const internalAnalyticsPlugins = [sceneChangeDetectionPlugin];

export function getDefaultInternalAnalyticsPluginConfigs(): AnalyticsPluginConfig[] {
  return internalAnalyticsPlugins.map(plugin => ({
    enabled: false,
    id: plugin.id,
    inputMetadata: {},
    status: undefined,
    msg: undefined,
  }));
}

export function getAnalyticsPluginInfoById(id: string) {
  const internalPlugin = internalAnalyticsPlugins.find(plugin => plugin.id === id);
  if (internalPlugin) {
    return internalPlugin;
  }
  // Extend this function to support custom plugins configured by the tenant
}
